import type { DeletePreview, LinkPreview } from '@/api/endpoints/pools'
import { languageLabel } from '@/features/scheduler/poolCards'

/**
 * O texto dos diálogos de confirmação de pool, a partir das prévias do
 * backend. Puro, para testar sem DOM. As regras são as do dono, em
 * docs/plan-video-pools.md §6.
 */

export type LineTone = 'neutral' | 'reserve' | 'danger'

export interface PreviewLine {
  count: number | null
  text: string
  tone: LineTone
}

function plural(n: number, one: string, many: string): string {
  return n === 1 ? one : many
}

/** As linhas do "Usar o pool X?" e o rótulo do botão. */
export function linkPreviewLines(p: LinkPreview): { lines: PreviewLine[]; confirmLabel: string; blocked: boolean } {
  const lines: PreviewLine[] = []
  if (p.to_pool > 0) {
    lines.push({
      count: p.to_pool,
      text: `${plural(p.to_pool, 'vídeo não publicado, sem marca, vai', 'vídeos não publicados, sem marca, vão')} para o pool e fica${p.to_pool === 1 ? '' : 'm'} livre${p.to_pool === 1 ? '' : 's'} para os canais dele.`,
      tone: 'neutral',
    })
  }
  if (p.reserve > 0) {
    lines.push({
      count: p.reserve,
      text: `${plural(p.reserve, 'vídeo não publicado com a marca do canal fica', 'vídeos não publicados com a marca do canal ficam')} como reserva e sai${p.reserve === 1 ? '' : 'em'} antes dos vídeos do pool.`,
      tone: 'reserve',
    })
  }
  if (p.published > 0) {
    lines.push({
      count: p.published,
      text: `${plural(p.published, 'vídeo publicado continua', 'vídeos publicados continuam')} no canal e passa${p.published === 1 ? '' : 'm'} a contar no acervo do pool.`,
      tone: 'neutral',
    })
  }
  lines.push({ count: null, text: 'A geração própria do canal para no próximo ciclo.', tone: 'neutral' })
  if (p.removed > 0) {
    lines.push({
      count: p.removed,
      text: `${plural(p.removed, 'vídeo', 'vídeos')} em outro idioma ${plural(p.removed, 'será removido', 'serão removidos')} do servidor. O pool é ${languageLabel(p.pool_language)}, e vídeos em outro idioma não podem acompanhar o canal.`,
      tone: 'danger',
    })
  }
  const blocked = p.blocked > 0
  return {
    lines,
    blocked,
    confirmLabel: p.removed > 0 ? `Remover ${String(p.removed)} e usar o pool` : 'Usar o pool',
  }
}

/** As linhas do "Apagar o pool X?". */
export function deletePreviewLines(p: DeletePreview): PreviewLine[] {
  const lines: PreviewLine[] = [
    {
      count: p.unpublished,
      text:
        p.unpublished === 0
          ? 'Nenhum vídeo não publicado no pool.'
          : `${plural(p.unpublished, 'vídeo não publicado será apagado', 'vídeos não publicados serão apagados')} do servidor. Não dá para desfazer.`,
      tone: p.unpublished > 0 ? 'danger' : 'neutral',
    },
    {
      count: p.published,
      text: `${plural(p.published, 'vídeo publicado fica', 'vídeos publicados ficam')} com os canais que os publicaram.`,
      tone: 'neutral',
    },
  ]
  if (p.members.length > 0) {
    lines.push({
      count: p.members.length,
      text: `${plural(p.members.length, 'canal passa', 'canais passam')} a gerar sozinho${p.members.length === 1 ? '' : 's'}, com uma cópia da configuração deste pool, e volta${p.members.length === 1 ? '' : 'm'} à marca que tinha${p.members.length === 1 ? '' : 'm'} antes de entrar.`,
      tone: 'neutral',
    })
  }
  return lines
}

/** O nome digitado confere com o do pool (sem diferença de caixa ou espaço nas pontas). */
export function confirmsName(typed: string, name: string): boolean {
  return typed.trim().toLocaleLowerCase('pt-BR') === name.trim().toLocaleLowerCase('pt-BR') && name.trim() !== ''
}
